import { describeSwitchCase, SWITCH_ELSE_PORT, switchCasePort } from '../graphPorts.js'
import { PORT_KIND } from './ports.js'

/**
 * Flow nodes of the event/action graph: they sit between an event and its
 * actions and run on the device (docs/GPIO-LOGIC-FIRMWARE-SPEC.md §6).
 *
 * Both compare a subject: the UI object or GPIO entity wired to `subject`,
 * else the value of the event that triggered them (`{val}`, `{text}`, …).
 * `onChange` runs their actions only when the outcome differs from the
 * previous evaluation (for example an ADC value crossing a threshold).
 */

export const CONDITION_OPERATORS = Object.freeze(['==', '!=', '>', '>=', '<', '<='])

// `hint`: what a port is for, shown when hovering it on the canvas.
const TRIGGER_INPUT = Object.freeze({
    id: 'trigger', label: 'Trigger', accepts: [PORT_KIND.TRIGGER],
    hint: 'When to compare: wire an event, or a branch of a condition or switch',
})
const SUBJECT_INPUT = Object.freeze({
    id: 'subject', label: 'Compare', accepts: [PORT_KIND.OBJECT, PORT_KIND.GPIO_STATE], max: 1,
    hint: 'What to compare: an object or the State of a GPIO entity; unwired, the value of the triggering event',
})
const CASE_HINT = 'Runs when this is the first case that matches'
const CASE_OUTPUT = (id, label, hint = CASE_HINT) => ({ id, label, provides: PORT_KIND.TRIGGER, hint })
const ELSE_OUTPUT = CASE_OUTPUT(SWITCH_ELSE_PORT, 'Else', 'Runs when no case matches')

/** Condition: runs its `then` nodes when all of its conditions hold. */
export const conditionNode = {
    kind: 'condition',
    label: 'Condition',
    icon: '?',
    color: '#a16207',
    ports: {
        inputs: [TRIGGER_INPUT, SUBJECT_INPUT],
        outputs: [{ id: 'then', label: 'Then', provides: PORT_KIND.TRIGGER, hint: 'Runs when all conditions hold' }],
    },
    properties: {
        conditions: { type: 'list', label: 'Conditions' },
        onChange: { type: 'boolean', label: 'Only when the outcome changes' },
    },
    defaults: { conditions: [{ id: 'c1', property: 'val', operator: '==', value: '1' }], onChange: false },
}

/**
 * Switch: compares one property of its subject with its cases in order; the
 * nodes wired to the first matching case run, those on `else` when none
 * matches. One output per case (see portsFor), then `else`.
 */
export const switchNode = {
    kind: 'switch',
    label: 'Switch',
    icon: '⑂',
    color: '#9d174d',
    ports: {
        inputs: [TRIGGER_INPUT, SUBJECT_INPUT],
        outputs: [ELSE_OUTPUT],
    },
    portsFor: data => ({
        inputs: [TRIGGER_INPUT, SUBJECT_INPUT],
        outputs: [
            ...(data.cases ?? []).map(item => CASE_OUTPUT(switchCasePort(item.id), describeSwitchCase(item))),
            ELSE_OUTPUT,
        ],
    }),
    properties: {
        property: { type: 'string', label: 'Property' },
        cases: { type: 'list', label: 'Cases' },
        onChange: { type: 'boolean', label: 'Only when the case changes' },
    },
    defaults: { property: 'val', cases: [{ id: 'c1', operator: '==', value: '1' }], onChange: false },
}
